import React, { useState } from "react";
import { Box, List, ListItemButton, ListItemText, Typography, CircularProgress, Collapse } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTranslate } from "src/locales";
import { AuthorizedWarehouseDto } from "src/api/references/referenceServicesDtos";

interface SideBarNavigationProps {
  warehouses: AuthorizedWarehouseDto[];
  loading: boolean;
}

const SideBarNavigation: React.FC<SideBarNavigationProps> = ({ warehouses, loading }) => {
  const { t } = useTranslate();

  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null);
  const [isWarehousesExpanded, setIsWarehousesExpanded] = useState<boolean>(true);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("all");

  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setSelectedWarehouseId(null);
  };

  const handleWarehouseClick = (warehouseId: number) => {
    setSelectedWarehouseId(warehouseId);
    setSelectedCategoryId("");
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header: Depolar (Toggleable) */}
      <ListItemButton
        onClick={() => setIsWarehousesExpanded(!isWarehousesExpanded)}
        sx={{
          height: 52,
          borderRadius: "20px 20px 0 0",
          bgcolor: "rgba(240,240,240,0.6)",
          borderBottom: "3px solid #000",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, fontSize: 16, fontFamily: "Poppins, sans-serif" }}
        >
          {t("menus.stock-header")}
        </Typography>
        {isWarehousesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItemButton>

      <Collapse in={isWarehousesExpanded} timeout="auto">
        <Box sx={{ mt: 1 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List dense disablePadding>
              {warehouses.map((wh) => {
                const isActive = selectedWarehouseId === wh.id;
                return (
                  <ListItemButton
                    key={wh.id}
                    onClick={() => handleWarehouseClick(wh.id)}
                    sx={{
                      height: 52,
                      borderBottom: "1px solid rgba(0,0,0,0.12)",
                      borderRadius: 0,
                      px: 2,
                      "&:hover": {
                        bgcolor: "rgba(240,240,240,0.8)",
                      },
                    }}
                  >
                    <KeyboardArrowRightIcon
                      fontSize="small"
                      style={{ marginRight: 8, color: "rgba(0,0,0,0.54)" }}
                    />
                    <ListItemText
                      primary={wh.name}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? "#2196F3" : "text.primary",
                        fontFamily: "Poppins, sans-serif",
                      }}
                    />
                  </ListItemButton>
                );
              })}

              {!warehouses.length && !loading && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 2,
                    textAlign: "center",
                    fontSize: 13,
                    color: "text.secondary",
                  }}
                >
                  {t("common.no-data") || "Yetkili depo bulunamadı."}
                </Typography>
              )}
            </List>
          )}
        </Box>
      </Collapse>

      {/* Ekstra Kategoriler: Geçmiş, Sık Kullanılanlar, Tümü */}
      <Box sx={{ mt: 1 }}>
        <List dense disablePadding>
          {/* Geçmiş */}
          <ListItemButton
            onClick={() => handleCategoryClick("history")}
            sx={{
              height: 52,
              borderBottom: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 0,
              px: 2,
              bgcolor: selectedCategoryId === "history" ? "rgba(33, 150, 243, 0.15)" : "#F0F0F099",
              "&:hover": {
                bgcolor: "#F0F0F0",
              },
            }}
          >
            <KeyboardArrowRightIcon
              fontSize="small"
              sx={{ mr: 1, color: selectedCategoryId === "history" ? "#2196F3" : "rgba(0,0,0,0.54)" }}
            />
            <ListItemText
              primary={t("patient-request-service.categories.history") || "Geçmiş"}
              primaryTypographyProps={{
                fontSize: 14,
                fontWeight: selectedCategoryId === "history" ? 600 : 400,
                color: selectedCategoryId === "history" ? "#2196F3" : "text.primary",
                fontFamily: "Poppins, sans-serif",
              }}
            />
          </ListItemButton>

          {/* Sık Kullanılanlar */}
          <ListItemButton
            onClick={() => handleCategoryClick("frequent")}
            sx={{
              height: 52,
              borderBottom: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 0,
              px: 2,
              bgcolor: selectedCategoryId === "frequent" ? "rgba(33, 150, 243, 0.15)" : "#F0F0F099",
              "&:hover": {
                bgcolor: "#F0F0F0",
              },
            }}
          >
            <KeyboardArrowRightIcon
              fontSize="small"
              sx={{ mr: 1, color: selectedCategoryId === "frequent" ? "#2196F3" : "rgba(0,0,0,0.54)" }}
            />
            <ListItemText
              primary={t("patient-request-service.categories.frequent") || "Sık Kullanılanlar"}
              primaryTypographyProps={{
                fontSize: 14,
                fontWeight: selectedCategoryId === "frequent" ? 600 : 400,
                color: selectedCategoryId === "frequent" ? "#2196F3" : "text.primary",
                fontFamily: "Poppins, sans-serif",
              }}
            />
          </ListItemButton>

          {/* Tümü */}
          <ListItemButton
            onClick={() => handleCategoryClick("all")}
            sx={{
              height: 52,
              borderBottom: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 0,
              px: 2,
              bgcolor: selectedCategoryId === "all" ? "rgba(33, 150, 243, 0.15)" : "#F0F0F099",
              "&:hover": {
                bgcolor: "#F0F0F0",
              },
            }}
          >
            <KeyboardArrowRightIcon
              fontSize="small"
              sx={{ mr: 1, color: selectedCategoryId === "all" ? "#2196F3" : "rgba(0,0,0,0.54)" }}
            />
            <ListItemText
              primary={t("patient-request-service.categories.all") || "Tümü"}
              primaryTypographyProps={{
                fontSize: 14,
                fontWeight: selectedCategoryId === "all" ? 600 : 400,
                color: selectedCategoryId === "all" ? "#2196F3" : "text.primary",
                fontFamily: "Poppins, sans-serif",
              }}
            />
          </ListItemButton>
        </List>
      </Box>
    </Box>
  );
};

export default SideBarNavigation;


